import {unitFormats} from '@/lib/StandardFields';
import {getCompPresentation} from '@/services/ContentPresentation';

/**
 * Low-level display-field rules shared by shared-map field discovery (exportFields.ts)
 * and export formatting (exportFeatures.ts).
 */

/** A display field as handled by the export pipeline; may be a plain object or a DisplayField instance. */
export interface ExportDisplayField {
  label: string;
  value?: unknown;
  unit?: string;
  displayFields?: ExportDisplayField[];
  allowEdit?: boolean;
  allowLabelEdit?: boolean;
  allowDelete?: boolean;
}

/** A comp as handled by the export pipeline; carries feature-specific extras through the spread. */
export interface ExportComp {
  id?: string;
  type?: string;
  name?: string;
  displayFields?: ExportDisplayField[];
  link?: unknown;
  parent?: unknown;
  [key: string]: unknown;
}

/** One selectable field (or comp category, when `children` is present) in the export dialog. */
export interface ExportFieldSetting {
  label: string;
  value: boolean | 'indeterminate';
  collapsed?: boolean;
  children?: ExportFieldSetting[];
}

/** The export dialog's settings, keyed by tab id (collection name, comp type, or feature type). */
export type ExportFieldSettings = Map<string, ExportFieldSetting[]>;

export interface ExportSettingsTab {
  id: string;
  icon: {name: string};
}

/** The selections stamped onto an exported feature so a later refresh can re-apply them. */
export interface FieldSelections {
  displayFieldLabels: string[];
  sections?: Array<{section: string; displayFieldLabels: string[]}>;
}

/** The info-window content the marker format functions produce for a marker. */
export interface FormattedMarkerContent {
  title?: string;
  location: {lat(): number; lng(): number};
  displayFields?: ExportDisplayField[];
  comps?: ExportComp[];
}

/** The minimum zoom at which overlays are rendered. */
const MIN_OVERLAY_VISIBILITY_ZOOM = 13;

const excludedFieldNames = ['Last Modified By', 'SharePoint Folder', 'Brochure', 'Initial Site Analysis'];

/** Field labels that appear on several comps of one type; selections prefix them with the comp name. */
const includeCompNameFields = ['Provider', 'Upgrade Reqs', 'Capacity', 'Line Size'];

// Legacy dotted competitive marker icons stay registered for back-compat with already-exported SMAPs,
// but new exports always use their flat (no-dot) counterparts.
const exportIconReplacements: Record<string, string> = {
  'Darker-Blue-Balloon': 'Blue-Balloon-Flat',
  'Darker-Blue-Portfolio': 'Blue-Portfolio-Flat',
};
const normalizeIconForExport = (icon: string): string => exportIconReplacements[icon] ?? icon;

/** Checks if the unit is a numeric field */
const isNumericField = (unit?: string): boolean => {
  return ['number', 'feet', 'squareFeet', 'perSquareFoot', 'pricePerSquareFoot', 'pricePerAcre', 'pricePerFar', 'taxBill', 'currency', 'percentage', 'buildingCount', 'acreCount', 'acreage', 'miles'].includes(unit as string);
};

/** Converts a numeric field to a string */
const numericFieldToString = (field: ExportDisplayField): string => {
  const number = parseFloat(field.value as string);
  if (isNaN(number)) {
    return field.value as string;
  }
  return number.toLocaleString(undefined, {maximumFractionDigits: 2});
};

/** Retrieves the unit format info for this display field. */
const getUnitFormat = (unit?: string): {prefix?: string; suffix?: string} => {
  if (!((unit as string) in unitFormats)) {
    return {};
  }
  return unitFormats[unit as keyof typeof unitFormats];
};

/**
 * Recursively updates the display fields to be compatible with shared maps feature format
 * Returns the updated display fields
 */
const updateDisplayFields = (displayFields: ExportDisplayField[]): ExportDisplayField[] => {
  for (const field of displayFields) {
    // Allow edit and delete for all display fields
    field.allowEdit = true;
    field.allowLabelEdit = true;
    field.allowDelete = true;
    if (field.unit !== 'string') {
      // Convert the address display fields to a single string
      if (field.label === 'Address' || field.label === 'City, State') {
        let address = '';
        for (const subfield of field.displayFields!) {
          // Parcels saved before the rename store the zip as 'Zip'
          if (subfield.label === 'Zip Code' || subfield.label === 'Zip') {
            if (subfield.value) {
              address += ` ${subfield.value}`;
            }
            continue;
          }
          address += `${subfield.value}`;
          if (subfield.label !== 'State') {
            address += ', ';
          }
        }
        field.value = address;
      }

      if (isNumericField(field.unit)) {
        field.value = numericFieldToString(field);
      }
      const unit = getUnitFormat(field.unit);
      // Add the prefix and suffix to the text field value
      if (unit.prefix) {
        field.value = `${unit.prefix}${field.value}`;
      }
      if (unit.suffix) {
        field.value = `${field.value}${unit.suffix}`;
      }
      // Keep the unit type the same for links only
      if (field.unit !== 'link') {
        field.unit = 'string';
      }
    }
    if (field?.displayFields) {
      updateDisplayFields(field.displayFields);
    }
  }
  return displayFields;
};

/**
 * Given an Array of display fields, filters out:
 * - excluded fields
 * - fields with null/undefined/empty values
 * - fields with null values that have child display fields
 * It also formats display fields for any child display fields
 */
/* eslint-disable no-redeclare -- base rule misreads TypeScript overload signatures */
function filterDisplayFields(displayFields: ExportDisplayField[]): ExportDisplayField[];
function filterDisplayFields(displayFields?: ExportDisplayField[]): ExportDisplayField[] | undefined;
function filterDisplayFields(displayFields?: ExportDisplayField[]): ExportDisplayField[] | undefined {
  return displayFields?.filter((field) => {
    // If the field label should be excluded, exclude it
    if (excludedFieldNames.includes(field.label)) return false;

    // If the value is present and non-empty, include it
    if (field.value != null && String(field.value).trim() !== '') return true;

    // If the value is empty and there are no display fields, exclude it
    if (!field.displayFields) return false;

    // If the value is null and display fields exist, recursively filter the children display fields.
    // If that results in an empty array, exclude the field.
    field.displayFields = filterDisplayFields(field.displayFields);
    if (field.displayFields.length === 0) return false;

    return true;
  });
}
/* eslint-enable no-redeclare */

/**
 * Given an array of display fields, filters out fields that are not selected for export.
 * If `compName` is provided, it is prepended to the appropriate field labels.
 */
const filterExcludedFields = (
  displayFields: ExportDisplayField[],
  settings: ExportFieldSetting[],
  compName: string | null = null,
): ExportDisplayField[] => {
  const nameField = displayFields.find((field) => field.label === 'Name');
  return displayFields.filter((field) => {
    if (field.label === 'Name' || field.label === 'Building Name') return true;

    const setting = settings.find((setting) => {
      // Mimic the way the entries in settings are formatted
      const labelPrefix = `${compName ?? nameField?.value ?? ''}`;
      const dfLabel = includeCompNameFields.includes(field.label) ? `${labelPrefix} ${field.label}`.trim() : field.label;
      return setting.label === dfLabel;
    });
    return setting?.value === true;
  });
};

/**
 * Extracts display fields that were selected for export from the export settings.
 * This includes both top level fields and sectioned fields.
 */
const extractFieldSelectionsFromSettings = (exportSettings: ExportFieldSetting[]): FieldSelections => {
  const topLevelFieldSelections: string[] = [];
  const childFieldSelections: Array<{section: string; displayFieldLabels: string[]}> = [];

  exportSettings.forEach((setting) => {
    if (setting.children) {
      const selectedChildren = setting.children.filter((child) => child.value);
      if (selectedChildren.length) {
        childFieldSelections.push({
          section: setting.label,
          displayFieldLabels: selectedChildren.map((child) => child.label),
        });
      }
    } else if (setting.value) {
      topLevelFieldSelections.push(setting.label);
    }
  });

  const fieldSelections: FieldSelections = {
    displayFieldLabels: topLevelFieldSelections,
  };
  if (childFieldSelections.length) {
    fieldSelections.sections = childFieldSelections;
  }
  return fieldSelections;
};

/**
 * Filters and formats a single comp's display fields against the parent feature's export settings.
 * When `excludeWhenCategoryMissing` is true and the feature has settings but no category for this
 * comp type, the comp is excluded (overlay-export behavior). `excluded` marks comps deselected
 * for export.
 */
const formatCompDisplayFieldsForExport = (
  comp: ExportComp,
  featureExportSettings?: ExportFieldSetting[],
  {excludeWhenCategoryMissing = false}: {excludeWhenCategoryMissing?: boolean} = {},
): {excluded: boolean; displayFields: ExportDisplayField[]} => {
  let filteredCompDisplayFields = filterDisplayFields(comp.displayFields);

  if (featureExportSettings) {
    const pluralizedName = getCompPresentation(comp.type as string).getPluralizedName();
    // Match comp categories only (have `children`); a top-level field could share the label.
    const compExportSettings = featureExportSettings.find(
      (setting): setting is ExportFieldSetting & {children: ExportFieldSetting[]} =>
        setting.label === pluralizedName && setting.children !== undefined,
    );
    if (compExportSettings) {
      // Exclude the comp from export if the comp type is not selected
      if (!compExportSettings.value) return {excluded: true, displayFields: []};
      filteredCompDisplayFields = filterExcludedFields(
        filteredCompDisplayFields!,
        compExportSettings.children,
        comp.name,
      );
    } else if (excludeWhenCategoryMissing) {
      return {excluded: true, displayFields: []};
    }
  }

  return {
    excluded: false,
    displayFields: filteredCompDisplayFields?.length ? updateDisplayFields(filteredCompDisplayFields) : [],
  };
};

export {
  MIN_OVERLAY_VISIBILITY_ZOOM,
  includeCompNameFields,
  normalizeIconForExport,
  updateDisplayFields,
  filterDisplayFields,
  filterExcludedFields,
  extractFieldSelectionsFromSettings,
  formatCompDisplayFieldsForExport,
};
